import { log, logErr } from './logger';
import { updateCommon } from '../core/dataLayer';
import { VisitorId } from '../core/visitorId';
import { VisitorIdOptions } from '../types/types';
import CookieConsent from '../core/cookieConsent';

/**
 * This function will check if a condition has happened,
 * if the value is true it will immediately return
 * else it will wait for a timeout then return
 * @param timeoutMillis - The time in milliseconds to wait
 * @param condition - Condition to check is something has change
 * @param callback - Function that will be called based on logic.
 * @param args - An array with arguments for the callback function
 */
export async function waitForUpdate<P, T>(timeoutMillis: number, condition: boolean, callback: (args: P) => T | undefined, args: P): Promise<T | undefined> {
  try {
    log(`waitForUpdate called with condition AWSMA.instanceCreated = ${condition}`);
    await conditionalWait(timeoutMillis, !condition);

    return callback.call(null, args);
  } catch (e) {
    logErr(e);
  }
}

/**
 * Callback to check if visitorId was already created
 * @param token - token returned by the AWSMA.visitorIdReady global accessible function
 */
export function tokenCallback(token: string): void {
  if (token) {
    const cookieConsent = CookieConsent.cookieReader.getConsentCookie();
    let tokenToUpdate = token;
    if (cookieConsent && !cookieConsent.functional) {
      tokenToUpdate = '';
      log('token is not updated due to functional cookies preferences being disabled');
    }

    log(`tokenCallback is executed to update data layer with d2cVisitorToken with value: ${tokenToUpdate}`);
    window.AWSMA.instanceCreated = true;
    updateCommon({
      d2cVisitorToken: tokenToUpdate,
    });
  }
}

/**
 * Callback function to execute that initializes the VisitorId class, which requests a new visitor token
 * and sets the token value in the data layer. If sCode is already initialized
 * on the web page, don't initialize the new VisitorId class.
 * @param opts - Options to pass to the VisitorId constructor
 * @returns - New VisitorId object
 */
export function initializeVisitorIdCallback({ getVisitorTokenAndHandleCallback }: VisitorIdOptions): VisitorId | undefined {
  if (isSCodeInitialized()) {
    return;
  }

  return new VisitorId({
    cookieAllowed: isCookieAllowed(),
    getVisitorTokenAndHandleCallback,
  });
}

/**
 * This function is an util that will help us wait a period of time or return immediately.
 * @param timeoutMillis - The time in milliseconds to wait
 * @param wait - condition to know if it should wait or return
 */
export function conditionalWait(timeoutMillis: number, wait: boolean): Promise<void> {
  return new Promise<void>((resolve) => {
    if (wait) {
      const timeoutId = setTimeout(() => {
        clearTimeout(timeoutId);
        resolve();
      }, timeoutMillis);
    } else {
      resolve();
    }
  });
}

/**
 * Determines if cookies are allowed to be set or read from the visitors browser based on the
 * cookie consent preferences set in Shortbread.
 *
 * If the consent cookie is not set, default to false
 * @returns - Boolean representing if cookies are allowed to be set or accessed
 */
export function isCookieAllowed(): boolean {
  const consentCookie = CookieConsent.cookieReader.getConsentCookie();

  return consentCookie?.functional ?? false;
}

/**
 * Determines if s_code script has been set on the web page indicated by the window.AWSMA.instanceCreated value.
 * @returns - Boolean representing if s_code script is initialized on the web page
 */
export function isSCodeInitialized(): boolean {
  return window.AWSMA.instanceCreated ?? false;
}

export class GlobalStorage {
  private static data: Record<string, any> = {};
  static PREVIOUS_PAGE_URL = 'previousPageURL';

  static set(key: string, value: any): void {
    this.data[key] = value;
  }

  static get(key: string): any {
    return this.data[key];
  }

  static delete(key: string): void {
    delete this.data[key];
  }
}
